import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { escapeCsv, downloadCsv } from '@/utils/tableUtils';
import { formatUserLabel } from '@/utils/userLabel';
import { preventEditNavigationIfUnconfirmed } from '@/utils/confirmEditApprovedRequirement';
import { Pagination } from '@/components/table/Pagination';
import { CsvDownloadButton, ExcelDownloadButton } from '@/components/table/CsvDownloadButton';
import SavedViewsToolbar from '@/components/SavedViewsToolbar';
import RequirementReviewCard from '@/components/RequirementReviewCard';
import {
  getMyPermissions,
  getSavedView,
  listCategories,
  listProjectMembers,
  listRequirementStatuses,
  listRequirements,
  listUsersOptional,
  listVerificationMethodsByProject,
  patchRequirementByProject,
  downloadRequirementsXlsx,
} from '@/api/client';
import type {
  Category,
  EffectivePermissions,
  ProjectMember,
  Requirement,
  RequirementPatchBody,
  RequirementsColumnId,
  RequirementsSortColumn,
  RequirementStatus,
  SavedView,
  User,
  VerificationMethod,
} from '@/api/types';
import {
  defaultQueryState,
  parseSavedViewDefinition,
  type RequirementsQueryState,
} from '@/utils/savedViewDefinition';
import { StatusBadge } from './StatusBadge';

function formatModified(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${h}:${min}`;
  } catch {
    return iso;
  }
}

function approvalLabel(state: string): string {
  return state.replace(/_/g, ' ').toUpperCase();
}

/** Comma-separated verification method titles for table display. */
function verificationMethodsText(ids: number[], methods: VerificationMethod[]): string {
  if (!ids.length) return '—';
  return ids
    .map((id) => methods.find((m) => m.id === id)?.title?.trim() || `#${id}`)
    .join(', ');
}

/** Single open inline editor in the requirements table. */
type RequirementTableEditCell =
  | { reqId: number; kind: 'title' }
  | { reqId: number; kind: 'category' }
  | { reqId: number; kind: 'status' }
  | { reqId: number; kind: 'verification_methods' }
  | { reqId: number; kind: 'author' };

/** Parent ids from list API (`parent_requirement_ids`) or legacy single `parent_id`. */
function parentRequirementIdsForDisplay(req: Requirement): number[] {
  const ids = req.parent_requirement_ids;
  if (ids != null && ids.length > 0) return ids;
  if (req.parent_id != null) return [req.parent_id];
  return [];
}

function mergeRequirementAfterPatch(req: Requirement, patch: RequirementPatchBody): Requirement {
  const next: Requirement = { ...req };
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.description !== undefined) next.description = patch.description;
  if (patch.status_id !== undefined) next.status_id = patch.status_id;
  if (patch.author_id !== undefined) next.author_id = patch.author_id;
  if (patch.reviewer_id !== undefined) next.reviewer_id = patch.reviewer_id;
  if (patch.category_id !== undefined) next.category_id = patch.category_id;
  if (patch.applicability_id !== undefined) next.applicability_id = patch.applicability_id;
  if (patch.verification_method_ids !== undefined) {
    next.verification_method_ids = [...patch.verification_method_ids];
  }
  if (patch.custom_fields?.length) {
    const byId = new Map((next.custom_fields ?? []).map((c) => [c.field_id, { ...c }]));
    for (const u of patch.custom_fields) {
      const cur = byId.get(u.field_id) ?? {
        field_id: u.field_id,
        label: '',
        value: null as string | null,
      };
      byId.set(u.field_id, { ...cur, value: u.value });
    }
    next.custom_fields = [...byId.values()];
  }
  return next;
}

type ViewMode = 'table' | 'list';

export default function RequirementsTable({
  projectId,
  basePath,
  globalSearch,
  setGlobalSearch,
  viewMode,
}: {
  projectId: number;
  basePath: string;
  globalSearch: string;
  setGlobalSearch: (q: string) => void;
  viewMode: ViewMode;
}) {
  const { csrfToken, dashboard } = useDashboard();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId =
    dashboard?.user &&
    typeof dashboard.user === 'object' &&
    dashboard.user !== null &&
    'id' in dashboard.user &&
    typeof (dashboard.user as { id: unknown }).id === 'number'
      ? (dashboard.user as { id: number }).id
      : null;
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [statuses, setStatuses] = useState<RequirementStatus[]>([]);
  const [users, setUsers] = useState<User[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [verificationMethods, setVerificationMethods] = useState<VerificationMethod[]>([]);
  const [perms, setPerms] = useState<EffectivePermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  /** Last server-aligned row per id — avoid PATCH on blur when unchanged (reduces suspect churn). */
  const baselineRef = useRef<Map<number, Requirement>>(new Map());

  const [statusFilter, setStatusFilter] = useState<'all' | number>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | number>('all');
  // Deep-link: ?approval=draft|reviewed|approved (e.g. from the start screen, issue #387).
  const [approvalFilter, setApprovalFilter] = useState<'all' | string>(() => {
    const requested = searchParams.get('approval');
    return requested && ['draft', 'reviewed', 'approved'].includes(requested) ? requested : 'all';
  });
  const [sortColumn, setSortColumn] = useState<RequirementsSortColumn>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [visibleColumns, setVisibleColumns] = useState<RequirementsColumnId[]>(
    () => defaultQueryState().columns,
  );
  const [selectedViewId, setSelectedViewId] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [editCell, setEditCell] = useState<RequirementTableEditCell | null>(null);
  const inlineEditRef = useRef<HTMLDivElement | null>(null);
  const deepLinkApplied = useRef(false);

  const queryState: RequirementsQueryState = useMemo(
    () => ({
      statusFilter,
      categoryFilter,
      approvalFilter,
      q: globalSearch,
      sortColumn,
      sortDir,
      columns: visibleColumns,
      viewMode,
      pageSize,
    }),
    [
      statusFilter,
      categoryFilter,
      approvalFilter,
      globalSearch,
      sortColumn,
      sortDir,
      visibleColumns,
      viewMode,
      pageSize,
    ],
  );

  const applyQueryState = useCallback(
    (state: RequirementsQueryState) => {
      setStatusFilter(state.statusFilter);
      setCategoryFilter(state.categoryFilter);
      setApprovalFilter(state.approvalFilter);
      setGlobalSearch(state.q);
      setSortColumn(state.sortColumn);
      setSortDir(state.sortDir);
      setVisibleColumns(state.columns);
      setPageSize(state.pageSize);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (state.viewMode === 'list') next.set('view', 'list');
          else next.delete('view');
          return next;
        },
        { replace: true },
      );
    },
    [setGlobalSearch, setSearchParams],
  );

  const onSelectView = useCallback(
    (view: SavedView | null) => {
      setSelectedViewId(view?.id ?? null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (view) next.set('saved_view', String(view.id));
          else next.delete('saved_view');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const colVisible = useCallback(
    (id: RequirementsColumnId) => visibleColumns.includes(id),
    [visibleColumns],
  );

  const toggleColumn = useCallback((id: RequirementsColumnId) => {
    setVisibleColumns((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter((c) => c !== id);
      }
      return [...prev, id];
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const listOpts = {
          status_id: statusFilter === 'all' ? undefined : statusFilter,
          category_id: categoryFilter === 'all' ? undefined : categoryFilter,
          approval_state: approvalFilter === 'all' ? undefined : approvalFilter,
          sort_column: sortColumn ?? undefined,
          sort_dir: sortColumn ? sortDir : undefined,
          view_id: selectedViewId ?? undefined,
        };
        const [reqs, st, u, cat, mem, methods, permRes] = await Promise.all([
          listRequirements(projectId, listOpts),
          listRequirementStatuses(),
          listUsersOptional(),
          listCategories(),
          listProjectMembers(projectId),
          listVerificationMethodsByProject(projectId),
          getMyPermissions(projectId).catch(() => null),
        ]);
        if (cancelled) return;
        baselineRef.current = new Map(reqs.map((r) => [r.id, { ...r }]));
        setRequirements(reqs);
        setStatuses(st);
        setUsers(u);
        setCategories(cat.filter((c) => c.project_id === projectId));
        setMembers(mem);
        setVerificationMethods(methods);
        setPerms(permRes);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    statusFilter,
    categoryFilter,
    approvalFilter,
    sortColumn,
    sortDir,
    selectedViewId,
  ]);

  // Deep-link: ?saved_view=<id>
  useEffect(() => {
    if (deepLinkApplied.current) return;
    const raw = searchParams.get('saved_view');
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    deepLinkApplied.current = true;
    let cancelled = false;
    (async () => {
      try {
        const view = await getSavedView(projectId, id);
        if (cancelled) return;
        setSelectedViewId(view.id);
        applyQueryState(parseSavedViewDefinition(view.definition));
      } catch {
        /* ignore missing / forbidden view */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, projectId, applyQueryState]);

  const statusById = useMemo(() => {
    const m = new Map<number, RequirementStatus>();
    for (const s of statuses) m.set(s.id, s);
    return m;
  }, [statuses]);

  const userLabel = useCallback(
    (id: number) => formatUserLabel(id, { members, users }),
    [members, users],
  );

  const categoryById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of categories) m.set(c.id, c.title);
    return m;
  }, [categories]);

  const memberUserIds = useMemo(
    () => [...new Set(members.map((m) => m.user_id))].sort((a, b) => a - b),
    [members],
  );

  /** Reference code (KEY) per requirement id — used for parent column display. */
  const reqKeyById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of requirements) {
      const key = (r.reference_code ?? '').trim();
      m.set(r.id, key || `#${r.id}`);
    }
    return m;
  }, [requirements]);

  const reqTitleById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of requirements) m.set(r.id, r.title);
    return m;
  }, [requirements]);

  const canEdit = Boolean(perms?.edit_requirements && (csrfToken ?? '').length);
  const canEditStatus = Boolean(
    perms?.edit_requirements && perms?.is_project_reviewer && (csrfToken ?? '').length,
  );

  const saveReq = useCallback(
    async (id: number, patch: RequirementPatchBody) => {
      const token = csrfToken ?? '';
      if (!token || !perms?.edit_requirements) return;
      if (patch.status_id !== undefined && !perms?.is_project_reviewer) return;
      setSaveErr(null);
      setEditCell((prev) => (prev?.reqId === id ? null : prev));
      setSavingId(id);
      try {
        await patchRequirementByProject(projectId, id, patch, token);
        setRequirements((prev) =>
          prev.map((r) => (r.id === id ? mergeRequirementAfterPatch(r, patch) : r)),
        );
        const base = baselineRef.current.get(id);
        if (base) {
          baselineRef.current.set(id, mergeRequirementAfterPatch(base, patch));
        }
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSavingId(null);
      }
    },
    [csrfToken, perms?.edit_requirements, perms?.is_project_reviewer, projectId],
  );

  const statusOptions = useMemo(() => {
    const forProject = statuses.filter((s) => s.project_id === projectId);
    return forProject.length > 0 ? forProject : statuses;
  }, [statuses, projectId]);

  const q = globalSearch.trim().toLowerCase();

  const filtered = useMemo(() => {
    let rows = requirements.filter((req) => {
      if (statusFilter !== 'all' && req.status_id !== statusFilter) return false;
      if (categoryFilter !== 'all' && req.category_id !== categoryFilter) return false;
      if (
        approvalFilter !== 'all' &&
        req.approval_state.toLowerCase() !== approvalFilter.toLowerCase()
      ) {
        return false;
      }
      if (!q) return true;
      const blob = [req.reference_code, req.title, req.description, String(req.id)]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });

    if (sortColumn) {
      const dir = sortDir === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const cmp = (left: string | number, right: string | number) => {
          if (left < right) return -1 * dir;
          if (left > right) return 1 * dir;
          return 0;
        };
        switch (sortColumn) {
          case 'key':
            return cmp(a.reference_code || '', b.reference_code || '');
          case 'title':
            return cmp(a.title.toLowerCase(), b.title.toLowerCase());
          case 'category':
            return cmp(
              (categoryById.get(a.category_id) ?? '').toLowerCase(),
              (categoryById.get(b.category_id) ?? '').toLowerCase(),
            );
          case 'status':
            return cmp(
              (statusById.get(a.status_id)?.title ?? '').toLowerCase(),
              (statusById.get(b.status_id)?.title ?? '').toLowerCase(),
            );
          case 'approval':
            return cmp(a.approval_state, b.approval_state);
          case 'modified':
            return cmp(a.update_date, b.update_date);
          case 'author':
            return cmp(userLabel(a.author_id).toLowerCase(), userLabel(b.author_id).toLowerCase());
          default:
            return 0;
        }
      });
    }
    return rows;
  }, [
    requirements,
    statusFilter,
    categoryFilter,
    approvalFilter,
    q,
    sortColumn,
    sortDir,
    categoryById,
    statusById,
    userLabel,
  ]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, categoryFilter, approvalFilter, q, pageSize, sortColumn, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const sliceStart = (safePage - 1) * pageSize;
  const pageRows = useMemo(
    () => filtered.slice(sliceStart, sliceStart + pageSize),
    [filtered, sliceStart, pageSize],
  );

  useEffect(() => {
    setEditCell(null);
  }, [safePage, pageSize]);

  useEffect(() => {
    setEditCell(null);
  }, [viewMode]);

  useEffect(() => {
    if (editCell === null) return;
    const onDown = (e: MouseEvent) => {
      if (inlineEditRef.current?.contains(e.target as Node)) return;
      setEditCell(null);
    };
    const t = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [editCell]);

  useEffect(() => {
    if (editCell === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditCell(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editCell]);

  useEffect(() => {
    if (editCell === null) return;
    const id = requestAnimationFrame(() => {
      const el = inlineEditRef.current?.querySelector<HTMLElement>(
        'input:not([type="checkbox"]), select, textarea',
      );
      el?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [editCell]);

  const resetFilters = useCallback(() => {
    setStatusFilter('all');
    setCategoryFilter('all');
    setApprovalFilter('all');
    setSortColumn(null);
    setSortDir('asc');
  }, []);

  const exportCsv = useCallback(() => {
    const headers = [
      'Key',
      'Title',
      'Category',
      'Parent ids',
      'Status',
      'Approval',
      'Verification method ids',
      'Modified',
      'Author',
    ];
    const rows = filtered.map((req) => {
      const st = statusById.get(req.status_id);
      return [
        escapeCsv(req.reference_code || `#${req.id}`),
        escapeCsv(req.title),
        escapeCsv(categoryById.get(req.category_id) ?? ''),
        escapeCsv(parentRequirementIdsForDisplay(req).join(';')),
        escapeCsv(st?.title ?? ''),
        escapeCsv(req.approval_state),
        escapeCsv((req.verification_method_ids ?? []).join(';')),
        escapeCsv(formatModified(req.update_date)),
        escapeCsv(userLabel(req.author_id)),
      ];
    });
    downloadCsv(`requirements-project-${projectId}.csv`, headers, rows);
  }, [filtered, statusById, categoryById, userLabel, projectId]);

  const exportXlsx = useCallback(async () => {
    setExportErr(null);
    setExportingXlsx(true);
    try {
      await downloadRequirementsXlsx(projectId);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Excel export failed');
    } finally {
      setExportingXlsx(false);
    }
  }, [projectId]);

  if (loading) {
    return (
      <div className="p-8 text-center text-stitch-muted font-mono italic border border-stitch-border rounded-xl bg-stitch-surface">
        Loading registry…
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/25 text-red-200 text-sm">
        {err}
      </div>
    );
  }

  const cellInput =
    'w-full min-w-[90px] max-w-[min(100%,280px)] text-xs bg-stitch-elevated border border-stitch-border rounded-sm px-2 py-1.5 text-stitch-fg focus:border-stitch-accent outline-hidden disabled:opacity-50';
  const cellSelect = `${cellInput} cursor-pointer`;
  /** Collapsed cell: click to open editor */
  const displayCellBtn =
    'w-full text-left text-xs text-stitch-fg/90 leading-snug rounded-md px-1.5 py-1 hover:bg-stitch-higher border border-transparent hover:border-stitch-border/40 transition-colors min-h-7';
  const closeCellEdit = () => setEditCell(null);

  return (
    <div className="space-y-6">
      {saveErr ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-100 text-sm px-4 py-2">
          {saveErr}
        </div>
      ) : null}

      <SavedViewsToolbar
        projectId={projectId}
        currentUserId={currentUserId}
        query={queryState}
        selectedViewId={selectedViewId}
        onSelectView={onSelectView}
        onApplyState={applyQueryState}
      />

      {/* Filters bar — Image 2.html */}
      <div className="bg-stitch-elevated p-4 rounded-xl border border-stitch-border flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-stitch-surface border border-stitch-border rounded-sm text-xs text-stitch-muted">
            <span className="material-symbols-outlined text-sm">filter_list</span>
            <span>Status:</span>
            <select
              aria-label="Filter by status"
              value={statusFilter === 'all' ? 'all' : String(statusFilter)}
              onChange={(e) => {
                const v = e.target.value;
                setStatusFilter(v === 'all' ? 'all' : Number(v));
              }}
              className="bg-transparent text-stitch-accent font-bold text-xs border-none outline-hidden cursor-pointer"
            >
              <option value="all">All</option>
              {statusOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-stitch-surface border border-stitch-border rounded-sm text-xs text-stitch-muted">
            <span>Category:</span>
            <select
              aria-label="Filter by category"
              value={categoryFilter === 'all' ? 'all' : String(categoryFilter)}
              onChange={(e) => {
                const v = e.target.value;
                setCategoryFilter(v === 'all' ? 'all' : Number(v));
              }}
              className="bg-transparent text-stitch-accent font-bold text-xs border-none outline-hidden cursor-pointer"
            >
              <option value="all">All</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-stitch-surface border border-stitch-border rounded-sm text-xs text-stitch-muted">
            <span>Approval:</span>
            <select
              aria-label="Filter by approval"
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value)}
              className="bg-transparent text-stitch-accent font-bold text-xs border-none outline-hidden cursor-pointer"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="reviewed">Reviewed</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-stitch-surface border border-stitch-border rounded-sm text-xs text-stitch-muted">
            <span>Sort:</span>
            <select
              aria-label="Sort requirements"
              value={sortColumn ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSortColumn(v ? (v as Exclude<RequirementsSortColumn, null>) : null);
              }}
              className="bg-transparent text-stitch-accent font-bold text-xs border-none outline-hidden cursor-pointer"
            >
              <option value="">Default</option>
              <option value="key">Key</option>
              <option value="title">Title</option>
              <option value="category">Category</option>
              <option value="status">Status</option>
              <option value="approval">Approval</option>
              <option value="modified">Modified</option>
              <option value="author">Author</option>
            </select>
            <button
              type="button"
              disabled={!sortColumn}
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              className="text-stitch-accent font-bold disabled:opacity-40"
              title="Toggle sort direction"
            >
              {sortDir === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          {viewMode === 'table' ? (
            <details className="relative">
              <summary className="list-none cursor-pointer text-xs text-stitch-accent font-bold flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">view_column</span>
                Columns
              </summary>
              <div className="absolute z-20 mt-2 left-0 min-w-[160px] rounded-lg border border-stitch-border bg-stitch-surface p-2 shadow-stitch space-y-1">
                {(
                  [
                    ['key', 'Key'],
                    ['title', 'Title'],
                    ['category', 'Category'],
                    ['parents', 'Parents'],
                    ['status', 'Status'],
                    ['approval', 'Approval'],
                    ['verification', 'Verification'],
                    ['modified', 'Modified'],
                    ['author', 'Author'],
                    ['actions', 'Actions'],
                  ] as const
                ).map(([id, label]) => (
                  <label
                    key={id}
                    className="flex items-center gap-2 text-xs text-stitch-fg/90 px-1 py-0.5"
                  >
                    <input
                      type="checkbox"
                      checked={colVisible(id)}
                      onChange={() => toggleColumn(id)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </details>
          ) : null}
          <div className="hidden sm:block h-4 w-px bg-stitch-border mx-1" />
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-stitch-accent font-bold flex items-center gap-1 hover:underline"
          >
            <span className="material-symbols-outlined text-sm">restart_alt</span>
            Reset Filters
          </button>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-[10px] text-stitch-muted font-mono">
            {filtered.length.toLocaleString()} Requirements found
          </p>
          <CsvDownloadButton onClick={exportCsv} />
          <ExcelDownloadButton onClick={exportXlsx} busy={exportingXlsx} />
        </div>
      </div>

      {exportErr ? (
        <p className="mb-4 text-xs text-red-300" role="alert">
          {exportErr}
        </p>
      ) : null}

      {viewMode === 'list' ? (
        <ul aria-label="Requirements for review" className="space-y-3">
          {pageRows.map((req) => (
            <li key={req.id}>
              <RequirementReviewCard
                requirement={req}
                basePath={basePath}
                status={statusById.get(req.status_id)}
                category={categoryById.get(req.category_id) ?? `Category #${req.category_id}`}
                author={userLabel(req.author_id)}
                verificationMethods={verificationMethods}
                requirementKeyById={reqKeyById}
                requirementTitleById={reqTitleById}
                canEdit={canEdit}
              />
            </li>
          ))}
          {filtered.length === 0 ? (
            <li className="rounded-xl border border-stitch-border bg-stitch-surface p-8 text-center text-sm text-stitch-muted">
              No requirements match filters.
            </li>
          ) : null}
        </ul>
      ) : (
        <div className="bg-stitch-surface overflow-x-auto rounded-xl border border-stitch-border shadow-stitch">
          <table className="w-full text-left border-collapse min-w-[960px]">
            <thead>
              <tr className="border-b border-stitch-border bg-stitch-elevated">
                {colVisible('key') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider whitespace-nowrap sticky left-0 z-10 bg-stitch-elevated border-r border-stitch-border/60">
                  Key
                </th>
                )}
                {colVisible('title') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider min-w-[140px]">
                  Title
                </th>
                )}
                {colVisible('category') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider min-w-[120px]">
                  Category
                </th>
                )}
                {colVisible('parents') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider min-w-[120px]">
                  Parents
                </th>
                )}
                {colVisible('status') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider min-w-[110px]">
                  Status
                </th>
                )}
                {colVisible('approval') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider">
                  Approval
                </th>
                )}
                {colVisible('verification') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider min-w-[140px]">
                  Verification
                </th>
                )}
                {colVisible('modified') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider whitespace-nowrap">
                  Modified
                </th>
                )}
                {colVisible('author') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider min-w-[120px]">
                  Author
                </th>
                )}
                {colVisible('actions') && (
                <th className="px-2 py-3 text-[10px] font-bold text-stitch-muted uppercase tracking-wider sticky right-0 bg-stitch-elevated min-w-[88px]">
                  Actions
                </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-stitch-border">
              {pageRows.map((req) => {
                const methodIds = req.verification_method_ids ?? [];
                const parentIds = parentRequirementIdsForDisplay(req);
                const busy = savingId === req.id;
                return (
                  <tr key={req.id} className="hover:bg-white/3 transition-colors">
                    {colVisible('key') && (
                      <td className="px-2 py-2 align-top sticky left-0 z-1 bg-stitch-surface border-r border-stitch-border/60">
                      <span className="text-xs font-mono text-stitch-accent font-semibold whitespace-nowrap">
                        {req.reference_code || `REQ-${req.id}`}
                      </span>
                    </td>
                    )}
                    {colVisible('title') && (
                      <td className="px-2 py-2 align-top max-w-[min(280px,26vw)]">
                      {editCell?.reqId === req.id && editCell.kind === 'title' && canEdit && !busy ? (
                        <div ref={inlineEditRef}>
                          <input
                            className={cellInput}
                            value={req.title}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRequirements((prev) =>
                                prev.map((r) => (r.id === req.id ? { ...r, title: v } : r)),
                              );
                            }}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              const b = baselineRef.current.get(req.id);
                              if (b && v === b.title) {
                                closeCellEdit();
                                return;
                              }
                              void saveReq(req.id, { title: v });
                              closeCellEdit();
                            }}
                          />
                        </div>
                      ) : canEdit && !busy ? (
                        <button
                          type="button"
                          title="Click to edit title"
                          className={`${displayCellBtn} line-clamp-3`}
                          onClick={() => setEditCell({ reqId: req.id, kind: 'title' })}
                        >
                          {req.title.trim() ? req.title : '—'}
                        </button>
                      ) : (
                        <span className="text-xs text-stitch-fg/90 px-1.5 py-1 block line-clamp-3">
                          {req.title.trim() ? req.title : '—'}
                        </span>
                      )}
                    </td>
                    )}
                    {colVisible('category') && (
                      <td className="px-2 py-2 align-top">
                      {editCell?.reqId === req.id && editCell.kind === 'category' && canEdit && !busy ? (
                        <div ref={inlineEditRef}>
                          <select
                            className={cellSelect}
                            value={req.category_id}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setRequirements((prev) =>
                                prev.map((r) => (r.id === req.id ? { ...r, category_id: v } : r)),
                              );
                              void saveReq(req.id, { category_id: v });
                              closeCellEdit();
                            }}
                          >
                            {categories.map((c) => (
                              <option key={c.id} value={c.id} className="bg-stitch-surface">
                                {c.title}
                              </option>
                            ))}
                            {!categories.some((c) => c.id === req.category_id) && (
                              <option value={req.category_id} className="bg-stitch-surface">
                                Category #{req.category_id}
                              </option>
                            )}
                          </select>
                        </div>
                      ) : canEdit && !busy ? (
                        <button
                          type="button"
                          title="Click to edit category"
                          className={`${displayCellBtn} line-clamp-2`}
                          onClick={() => setEditCell({ reqId: req.id, kind: 'category' })}
                        >
                          {categoryById.get(req.category_id) ?? `Category #${req.category_id}`}
                        </button>
                      ) : (
                        <span className="text-xs text-stitch-fg/90 px-1.5 py-1 block line-clamp-2">
                          {categoryById.get(req.category_id) ?? `Category #${req.category_id}`}
                        </span>
                      )}
                    </td>
                    )}
                    {colVisible('parents') && (
                      <td className="px-2 py-2 align-top text-xs">
                      {parentIds.length === 0 ? (
                        <span className="text-stitch-muted">—</span>
                      ) : (
                        <ul className="space-y-1 list-none m-0 p-0 max-w-[200px]">
                          {parentIds.map((parentId) => (
                            <li key={parentId}>
                              <Link
                                to={`${basePath}/requirements/${parentId}`}
                                className="text-stitch-accent hover:underline line-clamp-2 block font-mono text-xs"
                                title={reqTitleById.get(parentId) ?? undefined}
                              >
                                {reqKeyById.get(parentId) ?? `REQ #${parentId}`}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    )}
                    {colVisible('status') && (
                      <td className="px-2 py-2 align-top">
                      {editCell?.reqId === req.id && editCell.kind === 'status' && canEditStatus && !busy ? (
                        <div ref={inlineEditRef}>
                          <select
                            className={cellSelect}
                            value={req.status_id}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setRequirements((prev) =>
                                prev.map((r) => (r.id === req.id ? { ...r, status_id: v } : r)),
                              );
                              void saveReq(req.id, { status_id: v });
                              closeCellEdit();
                            }}
                          >
                            {statusOptions.map((s) => (
                              <option key={s.id} value={s.id} className="bg-stitch-surface">
                                {s.title}
                              </option>
                            ))}
                            {!statusOptions.some((s) => s.id === req.status_id) && (
                              <option value={req.status_id} className="bg-stitch-surface">
                                Status #{req.status_id}
                              </option>
                            )}
                          </select>
                        </div>
                      ) : canEditStatus && !busy ? (
                        <button
                          type="button"
                          title="Click to edit status"
                          className={`${displayCellBtn} line-clamp-2 inline-flex items-center`}
                          onClick={() => setEditCell({ reqId: req.id, kind: 'status' })}
                        >
                          <StatusBadge
                            title={statusById.get(req.status_id)?.title ?? `Status #${req.status_id}`}
                            tagColor={statusById.get(req.status_id)?.tag_color}
                          />
                        </button>
                      ) : (
                        <span className="text-xs px-1.5 py-1 block line-clamp-2 inline-flex">
                          <StatusBadge
                            title={statusById.get(req.status_id)?.title ?? `Status #${req.status_id}`}
                            tagColor={statusById.get(req.status_id)?.tag_color}
                          />
                        </span>
                      )}
                    </td>
                    )}
                    {colVisible('approval') && (
                      <td className="px-2 py-2 align-top">
                      <span className="text-[10px] font-bold uppercase text-stitch-muted border border-stitch-border rounded-sm px-1.5 py-1 inline-block max-w-[120px] truncate">
                        {approvalLabel(req.approval_state)}
                      </span>
                    </td>
                    )}
                    {colVisible('verification') && (
                      <td className="px-2 py-2 align-top min-w-[140px] max-w-[min(320px,28vw)]">
                      {editCell?.reqId === req.id &&
                      editCell.kind === 'verification_methods' &&
                      canEdit &&
                      !busy ? (
                        <div ref={inlineEditRef} className="space-y-2">
                          <select
                            multiple
                            size={Math.min(
                              8,
                              Math.max(3, verificationMethods.length || 3),
                            )}
                            className={`${cellInput} min-h-[72px]`}
                            value={methodIds.map(String)}
                            aria-label="Verification methods"
                            onChange={(e) => {
                              const sel = [...e.target.selectedOptions].map((o) =>
                                Number(o.value),
                              );
                              setRequirements((prev) =>
                                prev.map((r) =>
                                  r.id === req.id ? { ...r, verification_method_ids: sel } : r,
                                ),
                              );
                              void saveReq(req.id, { verification_method_ids: sel });
                            }}
                          >
                            {verificationMethods.map((m) => (
                              <option key={m.id} value={m.id} className="bg-stitch-surface">
                                {m.title}
                              </option>
                            ))}
                          </select>
                          <p className="text-[10px] text-stitch-muted leading-snug">
                            Ctrl/Cmd+click to select several. Click outside or press Esc to close.
                          </p>
                          <button
                            type="button"
                            className="text-[10px] font-bold uppercase tracking-wider text-stitch-accent hover:underline"
                            onClick={closeCellEdit}
                          >
                            Done
                          </button>
                        </div>
                      ) : canEdit && !busy ? (
                        <button
                          type="button"
                          title="Click to change verification methods"
                          className={displayCellBtn}
                          onClick={() =>
                            setEditCell({ reqId: req.id, kind: 'verification_methods' })
                          }
                        >
                          {verificationMethodsText(methodIds, verificationMethods)}
                        </button>
                      ) : (
                        <span className="text-xs text-stitch-fg/90 leading-snug block px-1.5 py-1">
                          {verificationMethodsText(methodIds, verificationMethods)}
                        </span>
                      )}
                    </td>
                    )}
                    {colVisible('modified') && (
                      <td className="px-2 py-2 align-top text-[11px] text-stitch-muted font-mono whitespace-nowrap">
                      {formatModified(req.update_date)}
                    </td>
                    )}
                    {colVisible('author') && (
                      <td className="px-2 py-2 align-top">
                      {editCell?.reqId === req.id && editCell.kind === 'author' && canEdit && !busy ? (
                        <div ref={inlineEditRef}>
                          <select
                            className={cellSelect}
                            value={req.author_id}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setRequirements((prev) =>
                                prev.map((r) => (r.id === req.id ? { ...r, author_id: v } : r)),
                              );
                              void saveReq(req.id, { author_id: v });
                              closeCellEdit();
                            }}
                          >
                            {memberUserIds.map((id) => (
                              <option key={id} value={id} className="bg-stitch-surface">
                                {userLabel(id)}
                              </option>
                            ))}
                            {!memberUserIds.includes(req.author_id) && (
                              <option value={req.author_id} className="bg-stitch-surface">
                                {userLabel(req.author_id)}
                              </option>
                            )}
                          </select>
                        </div>
                      ) : canEdit && !busy ? (
                        <button
                          type="button"
                          title="Click to edit author"
                          className={`${displayCellBtn} line-clamp-2`}
                          onClick={() => setEditCell({ reqId: req.id, kind: 'author' })}
                        >
                          {userLabel(req.author_id)}
                        </button>
                      ) : (
                        <span className="text-xs text-stitch-fg/90 px-1.5 py-1 block line-clamp-2">
                          {userLabel(req.author_id)}
                        </span>
                      )}
                    </td>
                    )}
                    {colVisible('actions') && (
                      <td className="px-2 py-2 align-top sticky right-0 z-1 bg-stitch-surface border-l border-stitch-border/60">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`${basePath}/requirements/${req.id}`}
                          className="p-1.5 text-stitch-muted hover:text-stitch-accent"
                          title="View"
                        >
                          <span className="material-symbols-outlined text-lg">visibility</span>
                        </Link>
                        <Link
                          to={`${basePath}/requirements/${req.id}/edit`}
                          onClick={(event) =>
                            preventEditNavigationIfUnconfirmed(event, req.approval_state, req.id)
                          }
                          className="p-1.5 text-stitch-muted hover:text-stitch-accent"
                          title="Edit"
                        >
                          <span className="material-symbols-outlined text-lg">edit</span>
                        </Link>
                        {canEdit ? (
                          <Link
                            to={`${basePath}/requirements/new?from=${req.id}`}
                            className="p-1.5 text-stitch-muted hover:text-stitch-accent"
                            title="Duplicate"
                          >
                            <span className="material-symbols-outlined text-lg">content_copy</span>
                          </Link>
                        ) : null}
                      </div>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <p className="p-6 text-center text-stitch-muted text-sm">No requirements match filters.</p>
          )}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-stitch-muted">
            <span>Show rows:</span>
            <select
              aria-label="Requirements per page"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-stitch-elevated border border-stitch-border rounded-sm text-xs py-1 px-2 text-stitch-fg focus:ring-1 focus:ring-stitch-accent outline-hidden"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-xs text-stitch-muted font-medium">
              Showing{' '}
              <span className="text-stitch-fg font-bold">
                {filtered.length === 0 ? 0 : sliceStart + 1}-{Math.min(sliceStart + pageSize, filtered.length)}
              </span>{' '}
              of <span className="text-stitch-fg font-bold">{filtered.length}</span>
            </p>
            <Pagination
              page={safePage}
              pageCount={pageCount}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
